/**
 * El gesto de conectar dos nodos: los puertos que aparecen con la herramienta
 * conector activa, el arrastre con vista previa, el resaltado del destino y el
 * alta de la arista.
 *
 * Se separó de `CanvasView` porque es una unidad cerrada —todo lo que hace falta
 * para trazar una flecha con el puntero, y nada más— con una frontera angosta:
 * necesita la cámara, el store, la capa de aristas y saber qué tarjeta hay bajo
 * un punto. La selección y la herramienta activa siguen siendo del canvas, y
 * llegan acá por callback.
 */

import { type StoreApi } from "zustand/vanilla";
import { type DocState } from "../model/store.ts";
import { type Card, type EdgeEnd, type EdgeRoute, type EdgeSide } from "../model/types.ts";
import { type Camera } from "./Camera.ts";
import { type EdgeLayer } from "./EdgeLayer.ts";
import { EdgePorts, portAt, type ScreenRect } from "./EdgePorts.ts";
import { autoSides, routePath } from "./edgeGeometry.ts";

export interface ConnectGestureDeps {
  store: StoreApi<DocState>;
  camera: Camera;
  edgeLayer: EdgeLayer;
  /** La tarjeta bajo un punto de mundo, con las reglas de hit-test del canvas. */
  cardAt: (wx: number, wy: number) => Card | null;
  cardById: (id: string) => Card | undefined;
  /** Color con el que nacen las aristas nuevas (el activo de la paleta). */
  color: () => string;
  /** Ruta y puntas con las que nacen las aristas nuevas (lo último elegido). */
  edgeStyle: () => { route: EdgeRoute; fromEnd: EdgeEnd; toEnd: EdgeEnd };
  /** Se creó una arista: el canvas la selecciona y decide qué hacer con la
   *  herramienta activa. */
  onCreated: (edgeId: string) => void;
  /** Empezó un gesto: el canvas suelta lo que tuviera seleccionado. */
  onBegin: () => void;
  /**
   * Se soltó en el vacío, lejos del nodo de origen: el canvas crea ahí un nodo
   * nuevo y lo conecta. Es el flujo que permite diagramar sin volver a la barra.
   * Se pasa el lado de salida elegido para que la arista nazca ya clavada ahí.
   */
  onDropInEmpty: (wx: number, wy: number, fromNodeId: string, fromSide: EdgeSide) => void;
}

/**
 * Cuánto hay que alejarse del punto de partida, en px de PANTALLA, para que
 * soltar en el vacío cuente como "crear un nodo acá".
 *
 * Sin este piso, un click sobre un puerto —sin arrastrar— dejaría una caja
 * suelta cada vez que alguien tantea la interfaz.
 */
const DROP_MIN_DRAG_PX = 40;

/**
 * Qué tan cerca del borde de un contenedor (en px de PANTALLA) hay que estar
 * para que muestre sus puertos. Más adentro mandan los hijos: el contenedor
 * es enorme y sus puertos taparían a los de las tarjetas que contiene.
 */
const GROUP_BORDER_PX = 16;

/** Distancia en px de pantalla del punto al borde del rect (0 = sobre el borde). */
function distToBorder(rect: ScreenRect, sx: number, sy: number): number {
  const dx = Math.max(rect.x - sx, 0, sx - (rect.x + rect.width));
  const dy = Math.max(rect.y - sy, 0, sy - (rect.y + rect.height));
  if (dx !== 0 || dy !== 0) return Math.hypot(dx, dy);
  return Math.min(sx - rect.x, rect.x + rect.width - sx, sy - rect.y, rect.y + rect.height - sy);
}

export class ConnectGesture {
  /** Capa de pantalla con los puertos y el resaltado del destino. */
  readonly ports = new EdgePorts();

  /** Origen del conector en curso, mientras dura el gesto. */
  private from: { nodeId: string; side: EdgeSide } | null = null;
  /** El lado de origen lo eligió el usuario (agarró un puerto) y no se mueve. */
  private pinned = false;
  /** Nodo que se llevaría la conexión si se soltara ahora. */
  private targetId: string | null = null;
  /** Lado de llegada bajo el puntero (o el automático si no apunta a un puerto). */
  private targetSide: EdgeSide | null = null;
  /** Lados con los que se está previsualizando: son los que se guardan al soltar. */
  private lastSideA: EdgeSide | null = null;
  private lastSideB: EdgeSide | null = null;
  /** Dónde está el puntero ahora, en coordenadas de mundo y de pantalla. */
  private lastW = { x: 0, y: 0 };
  private startS = { x: 0, y: 0 };
  private lastS = { x: 0, y: 0 };
  /** Nodo cuyos puertos se están mostrando, y el puerto bajo el puntero. */
  private hoverNodeId: string | null = null;
  private hoverSide: EdgeSide | null = null;
  /**
   * Cuando el gesto está re-enganchando un extremo de una arista que ya existe
   * en vez de creando una nueva. `from` pasa a ser el extremo QUIETO.
   */
  private editing: { edgeId: string; end: "from" | "to" } | null = null;

  constructor(private deps: ConnectGestureDeps) {}

  /** Rect de una tarjeta en píxeles de pantalla, o null si ya no existe. */
  private screenRect(id: string): ScreenRect | null {
    const card = this.deps.cardById(id);
    if (!card) return null;
    const tl = this.deps.camera.worldToScreen(card.x, card.y);
    const zoom = this.deps.camera.zoom;
    return { x: tl.x, y: tl.y, width: card.width * zoom, height: card.height * zoom };
  }

  /** El puerto bajo el puntero, si los puertos están visibles y cae en uno. */
  portUnderPointer(sx: number, sy: number): { nodeId: string; side: EdgeSide } | null {
    if (!this.hoverNodeId || !this.hoverSide) return null;
    const rect = this.screenRect(this.hoverNodeId);
    if (!rect || portAt(rect, sx, sy) !== this.hoverSide) return null;
    return { nodeId: this.hoverNodeId, side: this.hoverSide };
  }

  /**
   * Decide de qué nodo mostrar los puertos y si el puntero cae sobre uno.
   *
   * Sólo se llama con `enabled` cuando la herramienta conector está activa:
   * en `select` no hay puertos al hover, para no tapar el contenido con ruido.
   *
   * Los contenedores también ofrecen puertos, pero sólo cerca de su borde:
   * su rect es enorme y adentro están los hijos con sus propios puertos, que
   * son los que mandan ahí (`cardAt` los devuelve primero).
   */
  hover(sx: number, sy: number, enabled: boolean): void {
    if (!enabled) {
      this.hoverNodeId = null;
      this.hoverSide = null;
      return;
    }

    // Si el puntero sigue cerca de un puerto del nodo actual, se mantiene: así
    // no se pierden justo al salir del rect para agarrar el punto de afuera.
    if (this.hoverNodeId) {
      const rect = this.screenRect(this.hoverNodeId);
      const side = rect ? portAt(rect, sx, sy) : null;
      if (side) {
        this.hoverSide = side;
        return;
      }
    }

    const w = this.deps.camera.screenToWorld(sx, sy);
    const card = this.deps.cardAt(w.x, w.y);
    if (!card) {
      this.hoverNodeId = null;
      this.hoverSide = null;
      return;
    }
    if (card.type === "group") {
      const rect = this.screenRect(card.id);
      if (!rect || distToBorder(rect, sx, sy) > GROUP_BORDER_PX) {
        this.hoverNodeId = null;
        this.hoverSide = null;
        return;
      }
    }
    this.hoverNodeId = card.id;
    const rect = this.screenRect(card.id);
    this.hoverSide = rect ? portAt(rect, sx, sy) : null;
  }

  /** Olvida el nodo bajo el puntero (al salir del canvas, por ejemplo). */
  clearHover(): void {
    this.hoverNodeId = null;
    this.hoverSide = null;
  }

  /** ¿Hay un gesto en curso? */
  get active(): boolean {
    return this.from !== null;
  }

  /**
   * Arrastrar un extremo de una arista existente para llevarlo a otro nodo.
   *
   * El extremo que NO se arrastra hace de origen, así que a partir de acá el
   * gesto es el mismo que el de crear: se previsualiza desde el nodo quieto
   * hasta el puntero y se cierra sobre el nodo que haya debajo. El lado quieto
   * conserva el que ya tenía (explícito o automático) para no mover lo que el
   * usuario no tocó.
   */
  beginReconnect(edgeId: string, end: "from" | "to"): void {
    const edge = this.deps.store.getState().doc.edges.find((e) => e.id === edgeId);
    if (!edge) return;
    const quietSide = end === "from" ? edge.toSide ?? null : edge.fromSide ?? null;
    this.begin(end === "from" ? edge.toNode : edge.fromNode, quietSide);
    this.editing = { edgeId, end };
  }

  begin(nodeId: string, side: EdgeSide | null, screenX = 0, screenY = 0): void {
    this.from = { nodeId, side: side ?? "right" };
    this.editing = null;
    this.startS = { x: screenX, y: screenY };
    this.lastS = { x: screenX, y: screenY };
    // Sin puerto explícito el lado se recalcula al arrastrar; con puerto queda
    // clavado, que es lo que el usuario acaba de pedir al agarrarlo.
    this.pinned = side !== null;
    this.targetId = null;
    this.targetSide = null;
    this.lastSideA = side;
    this.lastSideB = null;
    this.deps.onBegin();
  }

  /** Repinta la vista previa del conector y busca el nodo de destino. */
  update(wx: number, wy: number, screenX = 0, screenY = 0): void {
    const from = this.from;
    if (!from) return;
    this.lastW = { x: wx, y: wy };
    this.lastS = { x: screenX, y: screenY };
    const source = this.deps.cardById(from.nodeId);
    if (!source) return;

    const target = this.deps.cardAt(wx, wy);
    this.targetId = target && target.id !== from.nodeId ? target.id : null;

    const a = { x: source.x, y: source.y, width: source.width, height: source.height };
    let b = { x: wx, y: wy, width: 1, height: 1 };
    if (this.targetId) {
      const t = this.deps.cardById(this.targetId);
      if (t) b = { x: t.x, y: t.y, width: t.width, height: t.height };
    }

    const auto = autoSides(a, b);
    const sideA = this.pinned ? from.side : auto[0];
    if (!this.pinned) this.from = { nodeId: from.nodeId, side: auto[0] };
    // El lado de llegada respeta el puerto bajo el puntero: si se suelta sobre
    // un punto, ese es el que queda guardado; si no, el automático del momento.
    let sideB = auto[1];
    this.targetSide = null;
    if (this.targetId) {
      const targetRect = this.screenRect(this.targetId);
      const hovered = targetRect ? portAt(targetRect, screenX, screenY) : null;
      if (hovered) {
        sideB = hovered;
        this.targetSide = hovered;
      }
    }
    this.lastSideA = sideA;
    this.lastSideB = this.targetId ? sideB : null;
    this.deps.edgeLayer.paintPreview(
      routePath(a, sideA, b, sideB, this.deps.edgeStyle().route),
      this.deps.camera.zoom,
    );
  }

  /** Cierra el gesto: crea la arista si hay destino, y limpia. */
  commit(): void {
    const from = this.from;
    const targetId = this.targetId;
    const editing = this.editing;
    // Se capturan antes de limpiar: `cancel` los pone en null.
    const sideA = this.lastSideA ?? from?.side ?? "right";
    const sideB = this.lastSideB ?? this.targetSide ?? "left";
    const dropW = this.lastW;
    const dragged = Math.hypot(this.lastS.x - this.startS.x, this.lastS.y - this.startS.y);
    this.cancel();
    if (!from) return;

    if (!targetId) {
      // Soltar en el vacío después de un arrastre real crea un nodo ahí y lo
      // conecta, clavando la salida en el lado elegido. Re-enganchar no: soltar
      // afuera deja la arista como estaba.
      if (!editing && dragged >= DROP_MIN_DRAG_PX) {
        this.deps.onDropInEmpty(dropW.x, dropW.y, from.nodeId, sideA);
      }
      return;
    }

    if (editing) {
      // Re-enganche: el extremo movido queda clavado en el puerto donde se
      // soltó (o en el automático de ese momento); el quieto no se toca.
      this.deps.store.getState().updateEdge(
        editing.edgeId,
        editing.end === "from"
          ? { fromNode: targetId, fromSide: sideB }
          : { toNode: targetId, toSide: sideB },
      );
      return;
    }

    // Crear respeta los dos puntos: salida y llegada quedan explícitos para
    // que mover los nodos después no los reacomode solos. La ruta, las puntas
    // y el color son lo último elegido: el conector recuerda su estilo.
    const style = this.deps.edgeStyle();
    const edge = this.deps.store.getState().addEdge({
      fromNode: from.nodeId,
      toNode: targetId,
      fromSide: sideA,
      toSide: sideB,
      color: this.deps.color(),
      route: style.route,
      fromEnd: style.fromEnd,
      toEnd: style.toEnd,
    });
    if (edge) this.deps.onCreated(edge.id);
  }

  /** Aborta el gesto sin dejar nada (Escape, o soltar en el vacío). */
  cancel(): void {
    this.from = null;
    this.pinned = false;
    this.targetId = null;
    this.targetSide = null;
    this.lastSideA = null;
    this.lastSideB = null;
    this.editing = null;
    this.deps.edgeLayer.paintPreview(null, this.deps.camera.zoom);
  }

  /**
   * Puertos y resaltado de destino, en píxeles de pantalla. Corre por frame.
   *
   * `connecting` es si el gesto está en curso; `showPorts` es si la herramienta
   * conector está activa y el canvas está en reposo (sin otro gesto ni
   * edición). Los `endpoints` (manijas de la arista seleccionada) se muestran
   * siempre en reposo, con o sin herramienta conector: re-enganchar una flecha
   * que ya existe no es crear una nueva.
   */
  sync(
    connecting: boolean,
    showPorts: boolean,
    endpoints: { x: number; y: number }[] | null = null,
  ): void {
    if (connecting) {
      this.ports.update({
        node: this.from ? this.screenRect(this.from.nodeId) : null,
        active: this.from?.side ?? null,
        target: this.targetId ? this.screenRect(this.targetId) : null,
        targetActive: this.targetSide,
      });
      return;
    }
    if (!showPorts && (!endpoints || endpoints.length === 0)) {
      this.ports.hide();
      return;
    }
    this.ports.update({
      node: showPorts && this.hoverNodeId ? this.screenRect(this.hoverNodeId) : null,
      active: showPorts ? this.hoverSide : null,
      target: null,
      endpoints,
    });
  }

  destroy(): void {
    this.ports.destroy();
  }
}
